import {
  ContextPriority,
  ContextSource,
  MessageContextItemKind,
  SearchMode,
} from '@affine/graphql';

import type { MailMessageAttachment } from '../../utils/mail-selection-context';
import type {
  AutomationAttachment,
  CanvasSelectionAttachment,
  CodeSelectionAttachment,
  DatabaseAttachment,
  PdfContextAttachment,
  WorkflowAttachment,
} from '../ai-chat-content/type';
import type { JsonValue } from '../ai-chat-messages/type';
import type { AttachmentChip, FileChip } from './type';

export interface CanonicalDocContext {
  docId: string;
  docContent: string;
  docTitle?: string;
}

export interface CanonicalTagContext {
  tagId: string;
  title: string;
  docIds: string[];
  source?: string;
  priority?: string;
}

export interface CanonicalCollectionContext {
  collectionId: string;
  title: string;
  docIds: string[];
  source?: string;
  priority?: string;
}

export interface CanonicalMessageContextDisplay {
  title: string;
  subtitle?: string | null;
  icon?: string | null;
  preview?: string | null;
  unavailable?: boolean | null;
  unavailableReason?: string | null;
}

export interface CanonicalMessageContextItem {
  id: string;
  kind: MessageContextItemKind;
  label: string;
  subtitle?: string | null;
  source: ContextSource;
  priority: ContextPriority;
  display: CanonicalMessageContextDisplay;
  refs: Record<string, JsonValue>;
  payload?: Record<string, JsonValue> | null;
}

export interface CanonicalMessageContext {
  docId?: string;
  searchMode: SearchMode;
  items: CanonicalMessageContextItem[];
}

export interface CanonicalImageContextAttachment {
  id?: string;
  fileName?: string | null;
  blobId?: string | null;
  thumbnailBlobId?: string | null;
  uploadIndex?: number | null;
  thumbnailUploadIndex?: number | null;
  preview?: string | null;
  mime?: string | null;
  size?: number | null;
  width?: number | null;
  height?: number | null;
  source?: string;
  priority?: string;
}

export interface CanonicalPdfFileContextAttachment {
  id?: string;
  fileName: string;
  blobId?: string | null;
  thumbnailBlobId?: string | null;
  uploadIndex?: number | null;
  thumbnailUploadIndex?: number | null;
  thumbnailPreview?: string | null;
  mime?: string | null;
  size?: number | null;
  pageCount?: number | null;
  source?: string;
  priority?: string;
}

export interface CanonicalFileContextAttachment {
  id?: string;
  fileName: string;
  blobId?: string | null;
  fileId?: string | null;
  uploadIndex?: number | null;
  mime?: string | null;
  size?: number | null;
  state?: string | null;
  source?: string;
  priority?: string;
}

export interface CanonicalAttachmentContextRef {
  id?: string;
  sourceId: string;
  name: string;
  blobId?: string | null;
  sourceType?: string | null;
  state?: string | null;
  source?: string;
  priority?: string;
}

export interface BuildCanonicalMessageContextInput {
  docId?: string;
  searchMode?: SearchMode;
  imageAttachments?: CanonicalImageContextAttachment[];
  pdfFileAttachments?: CanonicalPdfFileContextAttachment[];
  fileAttachments?: CanonicalFileContextAttachment[];
  attachmentRefs?: CanonicalAttachmentContextRef[];
  databaseAttachments?: DatabaseAttachment[];
  automationAttachments?: AutomationAttachment[];
  workflowAttachments?: WorkflowAttachment[];
  codeSelections?: CodeSelectionAttachment[];
  mailAttachments?: MailMessageAttachment[];
  pdfContextAttachments?: PdfContextAttachment[];
  canvasSelections?: CanvasSelectionAttachment[];
  docContexts?: CanonicalDocContext[];
  tagContexts?: CanonicalTagContext[];
  collectionContexts?: CanonicalCollectionContext[];
}

const SOURCE_MAP: Record<string, ContextSource> = {
  focus: ContextSource.FOCUS,
  FOCUS: ContextSource.FOCUS,
  fullscreen: ContextSource.FULLSCREEN,
  FULLSCREEN: ContextSource.FULLSCREEN,
  paste: ContextSource.PASTE,
  PASTE: ContextSource.PASTE,
  'last-active': ContextSource.LAST_ACTIVE,
  LAST_ACTIVE: ContextSource.LAST_ACTIVE,
  'client-action': ContextSource.CLIENT_ACTION,
  CLIENT_ACTION: ContextSource.CLIENT_ACTION,
};

const PRIORITY_MAP: Record<string, ContextPriority> = {
  highest: ContextPriority.HIGHEST,
  HIGHEST: ContextPriority.HIGHEST,
  high: ContextPriority.HIGHEST,
  HIGH: ContextPriority.HIGHEST,
  mid: ContextPriority.MID,
  MID: ContextPriority.MID,
  lowest: ContextPriority.LOWEST,
  LOWEST: ContextPriority.LOWEST,
};

function contextSource(value: string | undefined, fallback: ContextSource) {
  return value ? (SOURCE_MAP[value] ?? fallback) : fallback;
}

function contextPriority(value: string | undefined, fallback: ContextPriority) {
  return value ? (PRIORITY_MAP[value] ?? fallback) : fallback;
}

function record<T extends object>(value: T): Record<string, JsonValue> {
  return value as unknown as Record<string, JsonValue>;
}

function nonEmptyText(value: string | null | undefined) {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function nonNegativeIndex(value: number | null | undefined) {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0
    ? value
    : undefined;
}

function item(input: CanonicalMessageContextItem) {
  return input;
}

export function fileContextAttachmentsFromChips(
  chips: readonly FileChip[]
): CanonicalFileContextAttachment[] {
  return chips.map(chip => ({
    id: chip.fileId ?? chip.blobId ?? chip.file.name,
    fileName: chip.file.name,
    blobId: chip.blobId ?? null,
    fileId: chip.fileId ?? null,
    mime: chip.file.type || null,
    size: chip.file.size || null,
    state: chip.state,
    source: 'paste',
    priority: 'mid',
  }));
}

export function attachmentContextRefsFromChips(
  chips: readonly AttachmentChip[]
): CanonicalAttachmentContextRef[] {
  return chips.map(chip => ({
    id: chip.sourceId,
    sourceId: chip.sourceId,
    name: chip.name,
    blobId: chip.sourceId,
    sourceType: 'blob',
    state: chip.state,
    source: 'paste',
    priority: 'mid',
  }));
}

export function buildCanonicalMessageContext(
  input: BuildCanonicalMessageContextInput
): CanonicalMessageContext {
  const items: CanonicalMessageContextItem[] = [];
  const seen = new Set<string>();

  const push = (next: CanonicalMessageContextItem) => {
    const key = `${next.kind}:${next.id}`;
    if (seen.has(key)) return;
    seen.add(key);
    items.push(next);
  };

  for (const image of input.imageAttachments ?? []) {
    const uploadIndex = nonNegativeIndex(image.uploadIndex);
    const thumbnailUploadIndex = nonNegativeIndex(image.thumbnailUploadIndex);
    const label = nonEmptyText(image.fileName) ?? 'Image';
    push(
      item({
        id:
          image.id ??
          `image:${image.blobId ?? uploadIndex ?? label}:${thumbnailUploadIndex ?? ''}`,
        kind: MessageContextItemKind.IMAGE,
        label,
        subtitle: image.mime ?? null,
        source: contextSource(image.source, ContextSource.PASTE),
        priority: contextPriority(image.priority, ContextPriority.MID),
        display: {
          title: label,
          subtitle: image.mime ?? null,
          icon: 'image',
          preview: image.preview ?? null,
        },
        refs: record({
          ...(image.blobId ? { blobId: image.blobId } : {}),
          ...(image.thumbnailBlobId
            ? { thumbnailBlobId: image.thumbnailBlobId }
            : {}),
          ...(uploadIndex !== undefined ? { uploadIndex } : {}),
          ...(thumbnailUploadIndex !== undefined
            ? { thumbnailUploadIndex }
            : {}),
          ...(image.width ? { width: image.width } : {}),
          ...(image.height ? { height: image.height } : {}),
          ...(image.mime ? { mime: image.mime } : {}),
          ...(image.size ? { size: image.size } : {}),
        }),
        payload: record({
          fileName: label,
          mime: image.mime ?? null,
          size: image.size ?? null,
          width: image.width ?? null,
          height: image.height ?? null,
        }),
      })
    );
  }

  for (const pdf of input.pdfFileAttachments ?? []) {
    const uploadIndex = nonNegativeIndex(pdf.uploadIndex);
    const thumbnailUploadIndex = nonNegativeIndex(pdf.thumbnailUploadIndex);
    push(
      item({
        id: pdf.id ?? `pdf-file:${pdf.blobId ?? uploadIndex ?? pdf.fileName}`,
        kind: MessageContextItemKind.PDF_FILE,
        label: pdf.fileName,
        subtitle:
          typeof pdf.pageCount === 'number'
            ? `${pdf.pageCount} page(s)`
            : (pdf.mime ?? null),
        source: contextSource(pdf.source, ContextSource.PASTE),
        priority: contextPriority(pdf.priority, ContextPriority.MID),
        display: {
          title: pdf.fileName,
          subtitle:
            typeof pdf.pageCount === 'number'
              ? `${pdf.pageCount} page(s)`
              : (pdf.mime ?? null),
          icon: 'pdf',
          preview: pdf.thumbnailPreview ?? null,
        },
        refs: record({
          ...(pdf.blobId ? { blobId: pdf.blobId } : {}),
          ...(pdf.thumbnailBlobId
            ? { thumbnailBlobId: pdf.thumbnailBlobId }
            : {}),
          ...(uploadIndex !== undefined ? { uploadIndex } : {}),
          ...(thumbnailUploadIndex !== undefined
            ? { thumbnailUploadIndex }
            : {}),
          fileName: pdf.fileName,
          ...(pdf.pageCount ? { pageCount: pdf.pageCount } : {}),
          ...(pdf.mime ? { mime: pdf.mime } : {}),
          ...(pdf.size ? { size: pdf.size } : {}),
        }),
        payload: record({
          fileName: pdf.fileName,
          mime: pdf.mime ?? 'application/pdf',
          size: pdf.size ?? null,
          pageCount: pdf.pageCount ?? null,
        }),
      })
    );
  }

  for (const file of input.fileAttachments ?? []) {
    const uploadIndex = nonNegativeIndex(file.uploadIndex);
    push(
      item({
        id: file.id ?? `file:${file.fileId ?? file.blobId ?? file.fileName}`,
        kind: MessageContextItemKind.FILE,
        label: file.fileName,
        subtitle: file.mime ?? null,
        source: contextSource(file.source, ContextSource.PASTE),
        priority: contextPriority(file.priority, ContextPriority.MID),
        display: {
          title: file.fileName,
          subtitle: file.mime ?? null,
          icon: 'file',
          preview: null,
        },
        refs: record({
          ...(file.blobId ? { blobId: file.blobId } : {}),
          ...(file.fileId ? { fileId: file.fileId } : {}),
          ...(uploadIndex !== undefined ? { uploadIndex } : {}),
          fileName: file.fileName,
          ...(file.mime ? { mime: file.mime } : {}),
          ...(file.size ? { size: file.size } : {}),
        }),
        payload: record({
          fileName: file.fileName,
          mime: file.mime ?? null,
          size: file.size ?? null,
          state: file.state ?? null,
        }),
      })
    );
  }

  for (const attachment of input.attachmentRefs ?? []) {
    push(
      item({
        id: attachment.id ?? `attachment:${attachment.sourceId}`,
        kind: MessageContextItemKind.ATTACHMENT,
        label: attachment.name,
        subtitle: attachment.sourceType ?? null,
        source: contextSource(attachment.source, ContextSource.PASTE),
        priority: contextPriority(attachment.priority, ContextPriority.MID),
        display: {
          title: attachment.name,
          subtitle: attachment.sourceType ?? null,
          icon: 'attachment',
          preview: null,
        },
        refs: record({
          sourceId: attachment.sourceId,
          sourceType: attachment.sourceType ?? 'blob',
          ...(attachment.blobId ? { blobId: attachment.blobId } : {}),
        }),
        payload: record({
          name: attachment.name,
          state: attachment.state ?? null,
        }),
      })
    );
  }

  for (const doc of input.docContexts ?? []) {
    const title = nonEmptyText(doc.docTitle) ?? doc.docId;
    push(
      item({
        id: `doc:${doc.docId}`,
        kind: MessageContextItemKind.DOC,
        label: title,
        subtitle: null,
        source: ContextSource.FOCUS,
        priority: ContextPriority.MID,
        display: {
          title,
          subtitle: null,
          icon: 'doc',
          preview: nonEmptyText(doc.docContent) ?? null,
        },
        refs: record({ docId: doc.docId }),
        payload: record({
          docId: doc.docId,
          docTitle: title,
          docContent: doc.docContent,
        }),
      })
    );
  }

  for (const tag of input.tagContexts ?? []) {
    push(
      item({
        id: `tag:${tag.tagId}`,
        kind: MessageContextItemKind.TAG,
        label: tag.title,
        subtitle:
          tag.docIds.length > 0 ? `${tag.docIds.length} document(s)` : null,
        source: contextSource(tag.source, ContextSource.FOCUS),
        priority: contextPriority(tag.priority, ContextPriority.MID),
        display: {
          title: tag.title,
          subtitle:
            tag.docIds.length > 0 ? `${tag.docIds.length} document(s)` : null,
          icon: 'tag',
          preview: null,
        },
        refs: record({ tagId: tag.tagId }),
        payload: record({
          tagId: tag.tagId,
          title: tag.title,
          docIds: tag.docIds,
        }),
      })
    );
  }

  for (const collection of input.collectionContexts ?? []) {
    push(
      item({
        id: `collection:${collection.collectionId}`,
        kind: MessageContextItemKind.COLLECTION,
        label: collection.title,
        subtitle:
          collection.docIds.length > 0
            ? `${collection.docIds.length} document(s)`
            : null,
        source: contextSource(collection.source, ContextSource.FOCUS),
        priority: contextPriority(collection.priority, ContextPriority.MID),
        display: {
          title: collection.title,
          subtitle:
            collection.docIds.length > 0
              ? `${collection.docIds.length} document(s)`
              : null,
          icon: 'collection',
          preview: null,
        },
        refs: record({ collectionId: collection.collectionId }),
        payload: record({
          collectionId: collection.collectionId,
          title: collection.title,
          docIds: collection.docIds,
        }),
      })
    );
  }

  for (const database of input.databaseAttachments ?? []) {
    const databaseDocId = database.docId ?? input.docId;
    push(
      item({
        id: `database:${databaseDocId ?? 'unknown-doc'}:${database.blockId}`,
        kind: MessageContextItemKind.DATABASE,
        label: database.name,
        subtitle: database.selectedRowLabel ?? database.scope ?? null,
        source: contextSource(database.source, ContextSource.PASTE),
        priority: contextPriority(database.priority, ContextPriority.MID),
        display: {
          title: database.name,
          subtitle: database.selectedRowLabel ?? database.scope ?? null,
          icon: 'database',
          preview: null,
        },
        refs: record({
          blockId: database.blockId,
          ...(databaseDocId ? { docId: databaseDocId } : {}),
          ...(database.bounds ? { bounds: database.bounds } : {}),
        }),
        payload: record({
          ...database,
          docId: databaseDocId ?? null,
        }),
      })
    );
  }

  for (const automation of input.automationAttachments ?? []) {
    push(
      item({
        id: `automation:${automation.blockId}`,
        kind: MessageContextItemKind.AUTOMATION,
        label: automation.name,
        subtitle: automation.sourceTableName ?? null,
        source: contextSource(automation.source, ContextSource.PASTE),
        priority: contextPriority(automation.priority, ContextPriority.MID),
        display: {
          title: automation.name,
          subtitle: automation.sourceTableName ?? null,
          icon: 'automation',
          preview: null,
        },
        refs: record({
          blockId: automation.blockId,
          ...((automation.docId ?? input.docId)
            ? { docId: automation.docId ?? input.docId }
            : {}),
          ...(automation.bounds ? { bounds: automation.bounds } : {}),
          workflowId: automation.workflowId,
          sourceTableId: automation.sourceTableId,
        }),
        payload: record({
          ...automation,
          docId: automation.docId ?? input.docId ?? null,
        }),
      })
    );
  }

  for (const workflow of input.workflowAttachments ?? []) {
    const workflowLabel = workflow.workflowName
      ? `Workflow ${workflow.workflowName}`
      : 'Workflow';
    const workflowSubtitle =
      workflow.versionKey ??
      `${workflow.selectedNodes.length} selected node(s)`;
    push(
      item({
        id: `workflow:${workflow.workflowId}`,
        kind: MessageContextItemKind.WORKFLOW,
        label: workflowLabel,
        subtitle: workflowSubtitle,
        source: contextSource(workflow.source, ContextSource.FULLSCREEN),
        priority: contextPriority(workflow.priority, ContextPriority.HIGHEST),
        display: {
          title: workflowLabel,
          subtitle: workflowSubtitle,
          icon: 'workflow',
          preview: null,
        },
        refs: record({
          workflowId: workflow.workflowId,
          versionKey: workflow.versionKey,
          versionRole: workflow.versionRole,
          access: workflow.access,
          ...(input.docId ? { docId: input.docId } : {}),
        }),
        payload: record({ ...workflow }),
      })
    );
  }

  for (const selection of input.codeSelections ?? []) {
    const lineLabel =
      selection.fromLine === selection.toLine
        ? `Line ${selection.fromLine}`
        : `Lines ${selection.fromLine}-${selection.toLine}`;
    push(
      item({
        id: `code-selection:${selection.id}`,
        kind: MessageContextItemKind.CODE_SELECTION,
        label: selection.title,
        subtitle: `${selection.surface} - ${lineLabel}`,
        source: contextSource(selection.source, ContextSource.FOCUS),
        priority: contextPriority(selection.priority, ContextPriority.MID),
        display: {
          title: selection.title,
          subtitle: `${selection.surface} - ${lineLabel}`,
          icon: 'code',
          preview: nonEmptyText(selection.selectedText) ?? null,
        },
        refs: record({
          selectionId: selection.id,
          surface: selection.surface,
          ...(input.docId ? { docId: input.docId } : {}),
        }),
        payload: record({ ...selection }),
      })
    );
  }

  for (const pdf of input.pdfContextAttachments ?? []) {
    push(
      item({
        id: `pdf-block:${pdf.docId}:${pdf.blockId}`,
        kind: MessageContextItemKind.PDF_BLOCK,
        label: pdf.name,
        subtitle: null,
        source: contextSource(pdf.source, ContextSource.FOCUS),
        priority: contextPriority(pdf.priority, ContextPriority.MID),
        display: {
          title: pdf.name,
          subtitle: null,
          icon: 'pdf',
          preview: null,
        },
        refs: record({
          docId: pdf.docId,
          blockId: pdf.blockId,
        }),
        // New persisted context deliberately carries user-visible identity and
        // display metadata only. The backend resolves the live block mapping.
        payload: record({
          docId: pdf.docId,
          blockId: pdf.blockId,
          name: pdf.name,
          source: pdf.source,
          priority: pdf.priority,
        }),
      })
    );
  }

  for (const selection of input.canvasSelections ?? []) {
    push(
      item({
        id: `canvas-selection:${selection.docId}:${[
          ...selection.elementIds,
          ...selection.blockIds,
        ]
          .sort()
          .join(',')}`,
        kind: MessageContextItemKind.CANVAS_SELECTION,
        label: selection.label,
        subtitle: `${selection.items.length} selected item(s)`,
        source: contextSource(selection.source, ContextSource.PASTE),
        priority: contextPriority(selection.priority, ContextPriority.HIGHEST),
        display: {
          title: selection.label,
          subtitle: `${selection.items.length} selected item(s)`,
          icon: 'canvas',
          preview: null,
        },
        refs: record({
          docId: selection.docId,
          elementIds: selection.elementIds,
          blockIds: selection.blockIds,
        }),
        payload: record({ ...selection }),
      })
    );
  }

  for (const mail of input.mailAttachments ?? []) {
    const subtitle = [mail.sender, mail.dateLabel].filter(Boolean).join(' - ');
    push(
      item({
        id: `mail:${mail.id}`,
        kind: MessageContextItemKind.MAIL,
        label: mail.subject || '(ohne Betreff)',
        subtitle: subtitle || null,
        source: contextSource(mail.source, ContextSource.FULLSCREEN),
        priority: contextPriority(mail.priority, ContextPriority.MID),
        display: {
          title: mail.subject || '(ohne Betreff)',
          subtitle: subtitle || null,
          icon: 'mail',
          preview: mail.snippet ?? null,
        },
        refs: record({
          messageId: mail.id,
          workspaceId: mail.workspaceId,
          accountId: mail.accountId,
          folderId: mail.folderId,
        }),
        payload: record({ ...mail, folderId: mail.folderId ?? '' }),
      })
    );
  }

  return {
    ...(input.docId ? { docId: input.docId } : {}),
    searchMode: input.searchMode ?? SearchMode.AUTO,
    items,
  };
}
